import { type DocumentNode } from 'graphql';
import React, { createContext, useContext } from 'react';
import type { GraphQLContext, GraphQLProviderInput } from './interface';
import {
  PermissionsTransformer,
  UniqueFragmentsTransformer,
  UnusedVariableTransformer,
} from './transformers';
import { parseQuery } from './utils';

const gqlContext = createContext<GraphQLContext>({
  transform: (query: string | DocumentNode) =>
    typeof query === 'string' ? parseQuery(query) : query,
  permissions: undefined,
});

export const GraphQLProvider = ({
  children,
  enableTransform,
  fragmentsTransformer,
  onTransform,
  permissions,
  permissionsTransformer,
  unusedVariablesTransformer,
}: GraphQLProviderInput) => {
  const transformers = [
    new UniqueFragmentsTransformer(fragmentsTransformer),
    new PermissionsTransformer({
      enabled: enableTransform,
      onTransform,
      permissions,
      ...(permissionsTransformer ?? {}),
    }),
    new UnusedVariableTransformer(unusedVariablesTransformer),
  ];

  const transform = (inputQuery: string | DocumentNode): DocumentNode => {
    let query: DocumentNode = typeof inputQuery === 'string' ? parseQuery(inputQuery) : inputQuery;

    for (const transformer of transformers) {
      query = transformer.transform(query);
    }

    return query;
  };

  return <gqlContext.Provider value={{ transform, permissions }}>{children}</gqlContext.Provider>;
};

export const useGraphQLContext = (): GraphQLContext => useContext(gqlContext);
